import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight, Check, Loader2, Users } from "lucide-react";
import Seo, { absUrl } from "@/components/Seo";
import { Navbar } from "../components/landing/Navbar";
import { Footer } from "../components/landing/Footer";
import { Reveal } from "../components/landing/Reveal";
import { GroupQuoteModal } from "../components/booking/GroupQuoteModal";
import { useSiteData } from "../context/SiteData";
import { optImg } from "../lib/img";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const FleetDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { fleet, settings } = useSiteData();
  const [vehicle, setVehicle] = useState(null);
  const [state, setState] = useState("loading");
  const [quoteOpen, setQuoteOpen] = useState(false);

  useEffect(() => {
    setState("loading");
    setVehicle(null);
    axios
      .get(`${API}/fleet/${slug}`)
      .then(({ data }) => {
        setVehicle(data);
        setState("ok");
      })
      .catch(() => setState("404"));
    const lenis = window.__lenis;
    if (lenis) lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [slug]);

  if (state === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="fleet-detail-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--brand)]" />
      </div>
    );
  }

  if (state === "404" || !vehicle) {
    return (
      <div className="min-h-screen flex flex-col" data-testid="fleet-not-found">
        <Navbar />
        <div className="flex-1 flex flex-col items-center justify-center gap-5 px-5 pt-32 pb-20 text-center">
          <h1 className="font-display font-bold text-3xl sm:text-4xl text-[#0b132b]">Vehicle not found</h1>
          <button
            data-testid="fleet-not-found-back"
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-2 rounded-full bg-[var(--brand)] text-white text-sm font-bold px-6 py-3 shadow-lg shadow-[var(--brand)]/30 hover:bg-[var(--brand-dark)] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to homepage
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const related = fleet.filter((v) => v.id !== vehicle.id);
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: `${vehicle.name} — private hire with driver-guide`,
    description: vehicle.description || vehicle.copy,
    image: absUrl(vehicle.image),
    url: `${window.location.origin}/fleet/${vehicle.slug || vehicle.id}`,
    brand: { "@type": "Brand", name: "Voyage Switzerland" },
    ...(vehicle.price
      ? {
          offers: {
            "@type": "Offer",
            price: vehicle.price,
            priceCurrency: settings.currency || "CHF",
            availability: "https://schema.org/InStock",
            url: `${window.location.origin}/fleet/${vehicle.slug || vehicle.id}`,
          },
        }
      : {}),
  };

  return (
    <div data-testid="fleet-detail-page">
      <Seo
        title={`${vehicle.name} Hire in Switzerland — ${vehicle.capacity} | Voyage Switzerland`}
        siteName="Voyage Switzerland"
        description={`${vehicle.copy} Private hire with professional driver-guide across Switzerland.`}
        image={vehicle.image}
        jsonLd={jsonLd}
        alternates={{ en: `/fleet/${vehicle.slug || vehicle.id}` }}
        lang="en"
      />
      <Navbar />

      <section className="relative pt-[110px] sm:pt-[130px] pb-12 bg-[#f8fafc]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <button
            data-testid="fleet-detail-back"
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-2 text-sm font-bold text-slate-500 hover:text-[var(--brand)] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Our fleet
          </button>
          <div className="mt-6 grid lg:grid-cols-2 gap-10 items-center">
            <Reveal>
              <p className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-[var(--brand)]" data-testid="fleet-detail-capacity">
                <Users className="w-3.5 h-3.5" /> {vehicle.capacity}
              </p>
              <h1 className="mt-3 font-display font-bold tracking-tight text-4xl sm:text-5xl text-[#0b132b] leading-[1.05]" data-testid="fleet-detail-title">
                {vehicle.name}
              </h1>
              <p className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed">{vehicle.copy}</p>
              <button
                data-testid="fleet-detail-book-button"
                onClick={() => setQuoteOpen(true)}
                className="group mt-6 inline-flex items-center gap-2 rounded-full bg-[var(--brand)] text-white text-sm font-bold px-7 py-3.5 shadow-xl shadow-[var(--brand)]/30 transition-all duration-300 hover:bg-[var(--brand-dark)] hover:-translate-y-0.5"
              >
                Request This Vehicle <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="rounded-[2rem] overflow-hidden shadow-[0_24px_60px_-24px_rgba(11,19,43,0.3)]">
                <img src={optImg(vehicle.image, 1600)} alt={`${vehicle.name} — ${vehicle.capacity}, Voyage Switzerland fleet`} className="w-full h-[280px] sm:h-[400px] object-cover" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-14 sm:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          {vehicle.description && (
            <Reveal>
              <h2 className="font-display font-bold text-2xl sm:text-3xl text-[#0b132b]" data-testid="fleet-detail-about-title">
                About this vehicle
              </h2>
              <p className="mt-4 text-base text-slate-600 leading-relaxed" data-testid="fleet-detail-description">
                {vehicle.description}
              </p>
            </Reveal>
          )}
          {vehicle.features?.length > 0 && (
            <Reveal className="mt-10">
              <h2 className="font-display font-bold text-2xl sm:text-3xl text-[#0b132b]" data-testid="fleet-detail-features-title">
                On board
              </h2>
              <ul className="mt-5 grid sm:grid-cols-2 gap-3" data-testid="fleet-detail-features">
                {vehicle.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-3 rounded-2xl bg-[#f8fafc] border border-slate-100 px-4 py-3 text-sm font-semibold text-slate-700" data-testid={`fleet-feature-${i}`}>
                    <span className="mt-0.5 w-5 h-5 shrink-0 rounded-full bg-[var(--brand-soft)] flex items-center justify-center">
                      <Check className="w-3 h-3 text-[var(--brand)]" strokeWidth={3} />
                    </span>
                    {f}
                  </li>
                ))}
              </ul>
            </Reveal>
          )}
        </div>
      </section>

      {related.length > 0 && (
        <section className="py-16 bg-[#f8fafc] border-t border-slate-100" data-testid="fleet-detail-related">
          <div className="max-w-7xl mx-auto px-5 sm:px-8">
            <Reveal>
              <h2 className="font-display font-bold text-2xl sm:text-3xl text-[#0b132b]">Also in our fleet</h2>
            </Reveal>
            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {related.map((v) => (
                <Reveal key={v.id}>
                  <Link
                    to={`/fleet/${v.slug || v.id}`}
                    data-testid={`fleet-related-${v.slug || v.id}`}
                    className="group block rounded-3xl bg-white border border-slate-100 overflow-hidden shadow-[0_16px_40px_-20px_rgba(11,19,43,0.15)] hover:shadow-[0_30px_60px_-24px_rgba(225,29,72,0.35)] hover:border-[var(--brand)]/25 transition-all duration-300 hover:-translate-y-1.5"
                  >
                    <div className="h-40 overflow-hidden">
                      <img src={optImg(v.image, 800)} alt={`${v.name} — ${v.capacity}`} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    </div>
                    <div className="p-5">
                      <h3 className="font-display font-bold text-lg text-[#0b132b] group-hover:text-[var(--brand)] transition-colors">{v.name}</h3>
                      <p className="mt-1 text-xs text-slate-500">{v.capacity}</p>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
      <GroupQuoteModal
        open={quoteOpen}
        onClose={() => setQuoteOpen(false)}
        initialMessage={`Vehicle: ${vehicle.name} (${vehicle.capacity})`}
      />
    </div>
  );
};
